// src/context/AddAssetContext.tsx

import React, { createContext, useState, useContext, useMemo, useEffect, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { State } from '../state/reducers'; // Adjust path if needed
import { ApiSystem, System } from '../state/types'; // Adjust path if needed
import axios from 'axios';

const steps = ['Asset Information', 'Upload Images', 'Confirm & Save'];
export type ImageTypeKey = 'exterior' | 'manual' | 'pano' | 'uc';
export const UPLOAD_TYPES: ImageTypeKey[] = ['exterior', 'manual', 'pano', 'uc'];

// 1. Define the shape of the context's data and functions
interface AddAssetContextType {
  activeStep: number;
  handleNext: () => void;
  handleBack: () => void;
  handleRunVehicle: () => void;

  assetId: string;
  setAssetId: (value: string) => void;
  assetIdError: string;

  systems: System[];
  systemsLoading: boolean;
  selectedSystemCode: string;
  setSelectedSystemCode: (value: string) => void;

  configOptions: string[];
  selectedConfigName: string;
  setSelectedConfigName: (value: string) => void;
  noConfigsFound: boolean;

  // We'll need the full system/config objects later
  selectedSystem: System | null;
  selectedConfiguration: any | null; // Use a more specific type if you have one
  steps: string[]

  filesToUpload: File[];
  filesByType: Record<ImageTypeKey, File[]>;
  currentImageType: ImageTypeKey;
  setCurrentImageType: (type: ImageTypeKey) => void;
  addFiles: (files: File[], type: ImageTypeKey) => void;
  removeFile: (fileName: string, type: ImageTypeKey) => void;
  isUploading: boolean;
  allUploadsComplete: boolean;
  isFinalizing: boolean;
  uploadProgress: { [key: string]: number }


  imageTags: Record<ImageTypeKey, Record<string, string>>;
  setImageTag: (fileName: string, tag: string, type: ImageTypeKey) => void;
}

// 2. Create the Context
const AddAssetContext = createContext<AddAssetContextType | undefined>(undefined);

// 3. Create the Provider Component
interface AddAssetProviderProps {
  children: ReactNode;
  onClose: () => void; // Pass the modal's close function to the provider
}

export const AddAssetProvider: React.FC<AddAssetProviderProps> = ({ children, onClose }) => {
  // --- All state and logic from the modal is moved here ---
  const [activeStep, setActiveStep] = useState(0);
  const [assetId, setAssetId] = useState('');
  const [selectedSystemCode, setSelectedSystemCode] = useState('');
  const [selectedConfigName, setSelectedConfigName] = useState('');
  const [assetIdError, setAssetIdError] = useState('');
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [isUploading, setIsUploading] = useState(false);
  const [assetPk, setAssetPk] = useState<string | null>(null);
  const [currentImageType, setCurrentImageType] = useState<ImageTypeKey>('exterior');
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [filesByType, setFilesByType] = useState<Record<ImageTypeKey, File[]>>({
    exterior: [],
    manual: [],
    pano: [],
    uc: [],
  });

  const [imageTags, setImageTags] = useState<Record<ImageTypeKey, Record<string, string>>>({
    exterior: {},
    manual: {},
    pano: {},
    uc: {},
  });


  const filesToUpload = useMemo(() => Object.values(filesByType).flat(), [filesByType]);

  const { systems, selectedSystem: globallySelectedSystemCode, loading: systemsLoading } = useSelector((state: State) => state.systemState);

  // --- All derived data and effects also move here ---
  useEffect(() => {
    if (globallySelectedSystemCode) {
      setSelectedSystemCode(globallySelectedSystemCode);
    }
  }, [globallySelectedSystemCode]);

  const selectedSystem = useMemo(() => {
    if (!selectedSystemCode || !systems) return null;
    return systems.find((sys: ApiSystem) => sys.systemCode === selectedSystemCode) || null;
  }, [systems, selectedSystemCode]);

  const selectedConfiguration = useMemo(() => {
    return selectedSystem?.configurations?.[selectedConfigName] ?? null;
  }, [selectedSystem, selectedConfigName]);

  const vinRegexPattern = useMemo(() => {
    return selectedConfiguration?.clientProcess?.vin_regex;
  }, [selectedConfiguration]);

  const configOptions = useMemo(() => {
    return selectedSystem?.configurations ? Object.keys(selectedSystem.configurations) : [];
  }, [selectedSystem]);

  const noConfigsFound = !!(selectedSystem && configOptions.length === 0);

  const setImageTag = (fileName: string, tag: string, type: ImageTypeKey) => {
    setImageTags(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [fileName]: tag,
      }
    }));
  };

  useEffect(() => {
    // Only proceed if a system with configurations is selected
    if (selectedSystem && selectedSystem.configurations) {
      // Find the name of the configuration where clientProcess.is_default is true
      const defaultConfigName = Object.keys(selectedSystem.configurations).find(configName =>
        selectedSystem.configurations?.[configName]?.clientProcess?.is_default === true
      );

      // If a default is found, set it as the selected configuration
      if (defaultConfigName) {
        setSelectedConfigName(defaultConfigName);
      }
      // If no default is found, selectedConfigName remains as it is (usually an empty string),
      // forcing the user to make a manual selection.
    }
  }, [selectedSystem]);

  useEffect(() => {
    if (vinRegexPattern && assetId) {
      try {
        const regex = new RegExp(vinRegexPattern);
        setAssetIdError(regex.test(assetId) ? '' : `Make sure the asset ID matches ${vinRegexPattern}`);
      } catch (e) {
        setAssetIdError('Cannot validate: invalid regex in configuration.');
      }
    } else {
      setAssetIdError('');
    }
  }, [assetId, vinRegexPattern]);

  const allUploadsComplete = useMemo(() => {
    if (filesToUpload.length === 0) return false; // Not complete if there are no files
    return filesToUpload.every(file => uploadProgress[file.name] === 100);
  }, [filesToUpload, uploadProgress]);


  const addFiles = (newFiles: File[], type: ImageTypeKey) => {
    setFilesByType(prev => {
      const existingFileNames = new Set(prev[type].map(f => f.name));
      const uniqueNewFiles = newFiles.filter(f => !existingFileNames.has(f.name));
      return {
        ...prev,
        [type]: [...prev[type], ...uniqueNewFiles]
      };
    });
  };

  const removeFile = (fileNameToRemove: string, type: ImageTypeKey) => {
    setFilesByType(prev => ({
      ...prev,
      [type]: prev[type].filter(file => file.name !== fileNameToRemove)
    }));
    // Also remove from progress tracking if it exists
    setUploadProgress(prevProgress => {
      const newProgress = { ...prevProgress };
      delete newProgress[fileNameToRemove];
      return newProgress;
    });

    setImageTags(prev => {
      const newTagsForType = { ...prev[type] };
      delete newTagsForType[fileNameToRemove];
      return {
        ...prev,
        [type]: newTagsForType,
      };
    });

  };

  const uploadFilesToS3 = async (files: File[], presignedUrls: { [key: string]: string }) => {
    const uploadPromises = files.map(file => {
      const url = presignedUrls[file.name];
      if (!url) {
        console.error(`No presigned URL found for file: ${file.name}`);
        // Set error state for this file
        setUploadProgress(prev => ({ ...prev, [file.name]: -1 }));
        return Promise.resolve(); // Fail this promise
      }

      return axios.put(url, file, {
        headers: { 'Content-Type': file.type },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setUploadProgress(prev => ({ ...prev, [file.name]: percentCompleted }));
        },
      });
    });

    await Promise.all(uploadPromises);
    console.log("All uploads finished.");
  };

  const finalizeAsset = async () => {
    if (!assetPk) {
      alert("Cannot finalize: Asset Primary Key is missing.");
      return;
    }

    setIsFinalizing(true);
    try {
      // Create a simplified payload. The backend will use these filenames
      // to find the uploaded files and construct the full image objects.
      const payload = {
        pk: assetPk,
        imageExterior: filesByType.exterior.map(f => ({
          imageTag: imageTags.exterior[f.name] || 'Unassigned', // Look up the tag
          filename: f.name
        })),
        imageManual: filesByType.manual.map(f => ({
          imageTag: imageTags.manual[f.name] || 'N/A',
          filename: f.name
        })),
        imagePano: filesByType.pano.map(f => ({
          imageTag: imageTags.pano[f.name] || 'N/A',
          filename: f.name
        })),
        imageUndercarriage: filesByType.uc.map(f => ({
          imageTag: imageTags.uc[f.name] || 'N/A',
          filename: f.name
        })),
      };

      console.log("--- FINALIZING ASSET ---");
      console.log("Payload for finalize-asset lambda:", JSON.stringify(payload, null, 2));
      
      await axios.post(`${process.env.REACT_APP_UMS_API_BASE_URL}/asset/finalize`, payload);

      alert("Asset successfully created!");
      onClose(); // Close modal on success

    } catch (error) {
      console.error("Failed to finalize asset:", error);
      alert("An error occurred during finalization. Please check the console.");
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      // Logic for this step is purely validation. No API calls yet.
      if (vinRegexPattern) {
        const regex = new RegExp(vinRegexPattern);
        if (!regex.test(assetId)) {
          const continueAnyway = window.confirm(`This Asset ID does not match the required format (${vinRegexPattern}).\n\nContinue anyway?`);
          if (!continueAnyway) return;
        }
      }
      // If validation passes, just move to the next step.
      setActiveStep((prev) => prev + 1);
      return;
    }

    if (activeStep === 1) {
      if (filesToUpload.length === 0) {
        alert("Please select at least one file to upload before continuing.");
        return;
      }

      setIsUploading(true);
      setActiveStep((prev) => prev + 1);

      try {
        // 1. Prepare payload for our create-asset lambda
        const payloadFiles = Object.entries(filesByType).flatMap(([type, files]) =>
          files.map(file => ({
            filename: file.name,
            contentType: file.type,
            // Map our UI key to the backend key
            imageType: `${type}_raw` // e.g., 'exterior' -> 'exterior_raw'
          }))
        );

        const payload = {
          assetIdentifier: assetId,
          systemCode: selectedSystemCode,
          rooftopName: selectedConfigName,
          files: payloadFiles
        };

        // 2. Call the create-asset lambda to get presigned URLs
        const response = await axios.post(`${process.env.REACT_APP_UMS_API_BASE_URL}/asset/create`, payload);
        const { pk, presignedUrls } = response.data;

        setAssetPk(pk);

        // 3. Start the S3 uploads
        await uploadFilesToS3(filesToUpload, presignedUrls);

      } catch (error) {
        console.error("Failed to create asset or upload files", error);
        alert("An error occurred. Please check the console and try again.");
      } finally {
        setIsUploading(false);
      }
      return;
    }

    if (activeStep === steps.length - 1) {
      console.log(`--- FINALIZING ASSET ${assetPk} ---`);
      await finalizeAsset()
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (isUploading) return;
    setActiveStep((prev) => prev - 1);
  };

  const handleRunVehicle = async () => {
    const rooftopId = selectedConfiguration?.clientProcess?.id;

    if (!assetId || !selectedSystemCode || !rooftopId) {
      alert("VIN/Stock #, a selected system, and a configuration with a rooftop ID are required to run this action.");
      return;
    }

    try {
      const url = `${process.env.REACT_APP_UMS_API_BASE_URL}/run-vehicle`;
      const headers = {
        Authorization: 'bwdevenv' // Consider moving this to a more secure place
      };

      const payload = {
        vin: assetId,
        rooftop_id: rooftopId,
        system_code: `${selectedSystemCode}U03`
      };

      console.log("Triggering Run Vehicle with payload:", payload);
      await axios.post(url, payload, { headers });

      alert("Vehicle successfully triggered on the Odroid!");
      onClose(); // Close the modal on success
    } catch (err) {
      console.error("Error running vehicle", err);
      alert("Failed to run vehicle. See the console for more details.");
    }
  };


  // Bundle everything into the value for the provider
  const value = {
    activeStep,
    handleNext,
    handleBack,
    handleRunVehicle,
    assetId,
    setAssetId,
    assetIdError,
    systems: systems || [],
    systemsLoading,
    selectedSystemCode,
    setSelectedSystemCode,
    configOptions,
    selectedConfigName,
    setSelectedConfigName,
    noConfigsFound,
    selectedSystem,
    selectedConfiguration,
    steps,
    isUploading,
    filesByType,
    currentImageType,
    setCurrentImageType,
    addFiles,
    removeFile,
    allUploadsComplete,
    filesToUpload,
    isFinalizing,
    uploadProgress,
    imageTags,
    setImageTag
  };

  return <AddAssetContext.Provider value={value}>{children}</AddAssetContext.Provider>;
};

// 4. Create the custom hook for easy consumption
export const useAddAsset = (): AddAssetContextType => {
  const context = useContext(AddAssetContext);
  if (context === undefined) {
    throw new Error('useAddAsset must be used within an AddAssetProvider');
  }
  return context;
};